import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listAmbulanceItems,
  createAmbulanceItem,
  updateAmbulanceItem,
  deleteAmbulanceItem,
} from '../../api/ambulance.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { useAuth } from '../../auth/AuthProvider.jsx'

// In-app management of the ambulance package catalogue (AmbulanceServiceItem),
// replacing the Django-admin entry. The price set here is snapshotted onto each
// dispatch and drives the ambulance value delivered to subscribing orgs.
export default function AmbulanceCatalogue() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const isAdmin =
    user?.is_staff || user?.is_superuser || user?.role === 'admin'
  const myServiceId = user?.ambulance_profile?.id ?? null
  const params = !isAdmin && myServiceId ? { service: myServiceId } : {}

  const q = useQuery({
    queryKey: ['ambulance-items', params],
    queryFn: () => listAmbulanceItems(params),
  })
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['ambulance-items'] })

  const [form, setForm] = useState({ name: '', code: '', base_price_pgk: '' })
  const createMut = useMutation({
    mutationFn: createAmbulanceItem,
    onSuccess: () => {
      setForm({ name: '', code: '', base_price_pgk: '' })
      invalidate()
    },
  })
  const updateMut = useMutation({
    mutationFn: ({ id, patch }) => updateAmbulanceItem(id, patch),
    onSuccess: invalidate,
  })
  const deleteMut = useMutation({
    mutationFn: deleteAmbulanceItem,
    onSuccess: invalidate,
  })

  const items = q.data || []

  function add() {
    const name = form.name.trim()
    if (!name) return
    const payload = { name, code: form.code.trim() }
    const price = parseFloat(form.base_price_pgk)
    if (!Number.isNaN(price)) payload.base_price_pgk = price
    createMut.mutate(payload)
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      <Card>
        <CardHeader
          title="Ambulance package catalogue"
          subtitle="Packages your service offers (Emergency response, Inter-facility transfer, Standby cover, …). The price you set is snapshotted onto each dispatch and counts toward the value delivered to subscribing organisations."
        />
        {q.isError && (
          <div className="mt-2">
            <ErrorBanner error={q.error} />
          </div>
        )}
        <div className="grid gap-2 sm:grid-cols-4 mt-3">
          <input
            className="border rounded px-2 py-1.5 text-sm sm:col-span-2"
            placeholder="Package name *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <input
            className="border rounded px-2 py-1.5 text-sm"
            placeholder="Code (optional)"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
          />
          <input
            className="border rounded px-2 py-1.5 text-sm"
            type="number"
            placeholder="Price (PGK)"
            value={form.base_price_pgk}
            onChange={(e) =>
              setForm((f) => ({ ...f, base_price_pgk: e.target.value }))
            }
          />
        </div>
        {createMut.isError && (
          <div className="mt-2">
            <ErrorBanner error={createMut.error} />
          </div>
        )}
        <div className="mt-2 flex justify-end">
          <button
            onClick={add}
            disabled={createMut.isPending || !form.name.trim()}
            className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
          >
            {createMut.isPending ? 'Adding…' : 'Add package'}
          </button>
        </div>
      </Card>

      <Card>
        <CardHeader title={`Packages (${items.length})`} />
        {q.isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500">
                <th className="py-1">Package</th>
                {isAdmin && <th>Service</th>}
                <th>Code</th>
                <th className="text-right">Price (PGK)</th>
                <th className="text-center">Offered</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-sand-100">
                  <td className="py-1.5">{it.name}</td>
                  {isAdmin && (
                    <td className="text-slate-500">{it.service_name}</td>
                  )}
                  <td className="text-slate-500">{it.code || '—'}</td>
                  <td className="text-right">
                    <input
                      className="border rounded px-2 py-1 text-sm w-24 text-right"
                      type="number"
                      defaultValue={it.base_price_pgk ?? ''}
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value)
                        updateMut.mutate({
                          id: it.id,
                          patch: { base_price_pgk: Number.isNaN(v) ? null : v },
                        })
                      }}
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={it.is_offered}
                      onChange={(e) =>
                        updateMut.mutate({
                          id: it.id,
                          patch: { is_offered: e.target.checked },
                        })
                      }
                    />
                  </td>
                  <td className="text-right">
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete "${it.name}"?`))
                          deleteMut.mutate(it.id)
                      }}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={isAdmin ? 6 : 5}
                    className="py-3 text-slate-500 text-sm"
                  >
                    No packages yet — add one above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}
